import { imgProxyLoader } from '@doltech/utils/lib/image-loader';
import cl from 'classnames';
import Image from 'next/image';
import * as React from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../hooks/useDeviceQuery.hook';
import { Button } from '../ButtonV2/ButtonV2';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { Link } from '../Link';
import { LabelTag } from './components/LabelTag';
import { ResponsiveContainer, ResponsiveContainerProps } from './components/ResponsiveContainer';
import { COLOR, COLOR_PROPS } from './constants';
import { PromotionBannerActionModel, PromotionBannerContentProps } from './PromotionBannerType';

const MEDIUM_CONTAINER_MIN_WIDTH = 540;
const MAIN_INFO_MIN_WIDTH = 180;

const ThumbnailContainer = styled.div`
  background-color: #ffd1d4;
  width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;

  a {
    width: 100%;
  }
`;

const MainContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: minmax(${MAIN_INFO_MIN_WIDTH}px, 1fr);
  row-gap: 8px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 24px;

  .info {
    display: flex;
    flex-flow: row wrap-reverse;
    align-items: flex-end;
    gap: 12px;

    ${MainContainer} {
      flex: 1;
    }
  }

  &.medium {
    grid-auto-flow: column;
    column-gap: 16px;

    .info {
      flex-direction: row-reverse;
    }
  }
`;

const Container = styled.div`
  background-color: ${colorsV2.white100};
  padding: 16px;

  @media screen and ${deviceBreakpoints.fromTablet} {
    border-radius: 8px;
    padding: 24px;
  }
`;

export interface ThumbnailPromotionBannerProps extends ResponsiveContainerProps {
  thumbnail: string;
  label: string;
  title: string;
  renderContent: (contentProps: PromotionBannerContentProps) => React.ReactNode;
  action: PromotionBannerActionModel;
  color: typeof COLOR[keyof typeof COLOR];
  disabled?: boolean;
}

export const ThumbnailPromotionBanner = (props: ThumbnailPromotionBannerProps) => {
  const {
    thumbnail,
    label,
    title,
    renderContent,
    action,
    color,
    responsive,
    disabled = true,
    ...rest
  } = props;
  const [ref, { width }] = useMeasure();
  const isMediumContainer = React.useMemo(() => width > MEDIUM_CONTAINER_MIN_WIDTH, [width]);

  // TODO: Disable as requested by CEO and CTO. Remove or change default disabled to false when need to enable banner on all page.
  if (disabled) {
    return null;
  }

  return (
    <ResponsiveContainer ref={ref} className={responsive} {...rest}>
      <Container>
        <Layout className={cl({ medium: isMediumContainer })}>
          <div className="info">
            <MainContainer>
              {isMediumContainer && (
                <LabelTag className="tag" text={label} color={COLOR_PROPS[color].label} />
              )}

              <Typography.Title level={5} variant="semi-bold/16">
                {title}
              </Typography.Title>

              {renderContent({ isMediumContainer })}
            </MainContainer>

            <ThumbnailContainer>
              <Image
                width={120}
                height={80}
                alt={title}
                src={thumbnail}
                objectFit="cover"
                priority
                loader={imgProxyLoader}
              />
            </ThumbnailContainer>
          </div>

          <ActionContainer>
            <Link className="action" target="_blank" rel="nofollow" href={action.url}>
              {isMediumContainer ? (
                <Button.Primary variant="outline-red">
                  {action.tabletText || action.text}
                </Button.Primary>
              ) : (
                <Button.Primary variant="outline-red" fitParent>
                  {action.text}
                </Button.Primary>
              )}
            </Link>
          </ActionContainer>
        </Layout>
      </Container>
    </ResponsiveContainer>
  );
};
